import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function HomeOverlay({ onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    // Set a timeout to display the overlay after page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500); // Show overlay after 1.5 seconds

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isVisible) {
      // Sequence of animations
      const stageTimers = [
        setTimeout(() => setAnimationStage(1), 300),  // First content appears
        setTimeout(() => setAnimationStage(2), 600),  // Second content appears
      ];
      
      return () => stageTimers.forEach(timer => clearTimeout(timer));
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className="absolute left-[10%] md:left-[55%] xl:left-[5%] top-[0px] md:top-[50px] xl:top-[440px] z-5"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
      }}
    >
      {/* Desktop and Tablet Layout (in landscape orientation) */}
      <div 
        className="hidden md:flex w-[329px] md:w-[329px] xl:w-[548px] h-[190px] xl:h-[296px] rounded-[25px] border-[3px] border-[#910000] bg-[rgba(3,28,31,0.78)] p-5 xl:p-8 backdrop-blur-sm overflow-hidden relative"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#03181F]/30 to-transparent"></div>
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 xl:top-4 xl:right-4 text-white hover:text-[#C3EEFD] transition-colors z-20"
          aria-label="Close overlay"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 xl:w-6 xl:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content Container - Left side */}
        <div className="relative z-10 flex flex-col h-full flex-1 pr-3 xl:pr-4">
          {/* Main content */}
          <div 
            className="text-[1.6rem] xl:text-[2.6rem] leading-[2.2rem] xl:leading-[3.0rem] mb-auto transition-all duration-500 pt-3 xl:pt-6"
            style={{ 
              opacity: animationStage >= 1 ? 1 : 0,
              transform: animationStage >= 1 ? 'translateY(0)' : 'translateY(10px)', 
            }}
          >
            Big breakthrough by ThromBio scientists published in the world's premier scientific journal, <span className="font-bold">Nature</span>
          </div>
          
          {/* CTA Buttons */}
          <div 
            className="flex mt-2 xl:mt-4 mb-1 xl:mb-2 transition-all duration-500"
            style={{ 
              opacity: animationStage >= 2 ? 1 : 0,
              transform: animationStage >= 2 ? 'translateY(0)' : 'translateY(10px)', 
            }}
          >
          <Link 
            to="/media/nature"
            onClick={onClose}
            className="button bg-secondary-navy hover:!text-primary !no-underline !text-white normal-case text-[1.6rem] xl:text-[1.6rem] px-4 xl:px-6 py-2 xl:py-3"
          >
            Read More
          </Link>
          </div>
        </div>

        {/* Nature Journal Image - Right side */}
        <div className="relative z-10 flex items-center justify-center w-[110px] xl:w-[170px] h-full">
          <img 
            src="/nature-journal-cover.png" 
            alt="Nature Journal Cover" 
            className="max-w-full max-h-full object-contain rounded-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://www.nature.com/static/images/nature/nature-logo-header.png";
            }}
          />
        </div>
      </div>

      {/* Mobile Layout (in portrait orientation with same aspect ratio but axes swapped) */}
      <div 
        className="md:hidden w-[296px] h-[548px] rounded-[25px] border-[3px] border-[#910000] bg-[rgba(3,28,31,0.78)] p-6 backdrop-blur-sm overflow-hidden relative flex flex-col"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#03181F]/30 to-transparent"></div>
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[#C3EEFD] transition-colors z-20"
          aria-label="Close overlay"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Nature Journal Image - Top */}
        <div className="relative z-10 flex items-center justify-center w-full h-[300px] my-6">
          <img 
            src="/nature-journal-cover.png" 
            alt="Nature Journal Cover" 
            className="max-h-full max-w-full object-contain rounded-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://www.nature.com/static/images/nature/nature-logo-header.png";
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col flex-grow justify-between">
          {/* Main content */}
          <div 
            className="text-[2.0rem] leading-[2.6rem] transition-all duration-500"
            style={{ 
              opacity: animationStage >= 1 ? 1 : 0,
              transform: animationStage >= 1 ? 'translateY(0)' : 'translateY(10px)', 
            }}
          >
            Big breakthrough by ThromBio scientists published in the world's premier scientific journal, <span className="font-bold">Nature</span>
          </div>
          
         {/* CTA Buttons */}
          <div 
            className="flex mb-6 mt-6 transition-all duration-500"
            style={{ 
              opacity: animationStage >= 2 ? 1 : 0,
              transform: animationStage >= 2 ? 'translateY(0)' : 'translateY(10px)', 
            }}
          >
            <Link 
              to="/media/nature"
              onClick={onClose}
              className="button bg-secondary-navy hover:!text-primary !no-underline !text-white normal-case text-[2.0rem] px-6 py-3"
            >
              Read More
            </Link>
          </div>

          </div>
        </div>
      </div>
    
  );
}
